import { Environment } from '../environment/Environment';

interface Star { x: number; y: number; size: number; phase: number; }
interface Cloud { x: number; y: number; scale: number; speed: number; opacity: number; }
interface Particle { x: number; y: number; vx: number; vy: number; size: number; phase: number; }
interface Stone { x: number; y: number; rx: number; ry: number; color: string; dark: string; }
interface SmallFlower { x: number; y: number; color: string; size: number; phase: number; }
interface GrassTuft { x: number; blades: number; h: number; phase: number; }
interface Firefly { x: number; y: number; vx: number; vy: number; baseVx: number; baseVy: number; phase: number; speed: number; }
interface Petal { x: number; y: number; vx: number; vy: number; rot: number; rotSpeed: number; size: number; alpha: number; color: string; }
interface WetSpot { x: number; y: number; radius: number; alpha: number; droplets: Array<{ x: number; y: number; size: number; phase: number }>; }

export class EnvironmentRenderer {
  private ctx: CanvasRenderingContext2D;
  private soilCanvas: HTMLCanvasElement | null = null;
  private soilCtx: CanvasRenderingContext2D | null = null;
  private hillsCanvas: HTMLCanvasElement | null = null;
  private hillsCtx: CanvasRenderingContext2D | null = null;
  private width: number = 0;
  private height: number = 0;
  private soilDirty: boolean = true;
  private hillsDirty: boolean = true;

  private stars: Star[] = [];
  private clouds: Cloud[] = [];
  private particles: Particle[] = [];
  private stones: Stone[] = [];
  private flowers: SmallFlower[] = [];
  private grassTufts: GrassTuft[] = [];
  private starSeed: number = 123;
  private decorSeed: number = 456;

  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
    this.createSoilCanvas();
    this.createHillsCanvas();
    this.initStars();
    this.initClouds();
    this.initParticles();
    this.initFireflies();
  }

  private seededRandomFirefly(): number {
    this.fireflySeed = (this.fireflySeed * 9301 + 49297) % 233280;
    return this.fireflySeed / 233280;
  }

  private initFireflies(): void {
    this.fireflies = [];
    this.fireflySeed = 789;
    for (let i = 0; i < 20; i++) {
      const x = this.seededRandomFirefly();
      const y = 0.25 + this.seededRandomFirefly() * 0.35;
      const vx = (this.seededRandomFirefly() - 0.5) * 0.00015;
      const vy = (this.seededRandomFirefly() - 0.5) * 0.00008;
      this.fireflies.push({
        x, y, vx, vy, baseVx: vx, baseVy: vy,
        phase: this.seededRandomFirefly() * Math.PI * 2,
        speed: 0.8 + this.seededRandomFirefly() * 1.5
      });
    }
  }

  addWetSpot(x: number, y: number): void {
    const droplets: Array<{ x: number; y: number; size: number; phase: number }> = [];
    const count = 4 + Math.floor(Math.random() * 4);
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * 25;
      droplets.push({
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist * 0.5,
        size: 1 + Math.random() * 2,
        phase: Math.random() * Math.PI * 2
      });
    }
    this.wetSpots.push({
      x, y, radius: 40, alpha: 0.35,
      droplets
    });
  }

  spawnPetal(x: number, y: number): void {
    if (this.petals.length >= 15) return;
    const colors = ['#b898c8', '#c8a8d8', '#a888b8', '#d8b8e8', '#9a78a8'];
    this.petals.push({
      x,
      y: y - 80 - Math.random() * 60,
      vx: (Math.random() - 0.5) * 0.3,
      vy: 0.3 + Math.random() * 0.4,
      rot: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.02,
      size: 3 + Math.random() * 2,
      alpha: 0.6 + Math.random() * 0.3,
      color: colors[Math.floor(Math.random() * colors.length)]
    });
  }

  private createSoilCanvas(): void {
    this.soilCanvas = document.createElement('canvas');
    this.soilCtx = this.soilCanvas.getContext('2d');
  }

  private createHillsCanvas(): void {
    this.hillsCanvas = document.createElement('canvas');
    this.hillsCtx = this.hillsCanvas.getContext('2d');
  }

  private seededRandom(): number {
    this.starSeed = (this.starSeed * 9301 + 49297) % 233280;
    return this.starSeed / 233280;
  }

  private initStars(): void {
    this.stars = [];
    this.starSeed = 123;
    for (let i = 0; i < 80; i++) {
      this.stars.push({
        x: this.seededRandom(),
        y: this.seededRandom() * 0.55,
        size: 1 + this.seededRandom() * 2,
        phase: this.seededRandom() * Math.PI * 2
      });
    }
  }

  private initClouds(): void {
    this.clouds = [
      { x: 0.2, y: 0.15, scale: 1, speed: 0.0003, opacity: 0.7 },
      { x: 0.6, y: 0.22, scale: 0.8, speed: 0.0002, opacity: 0.6 },
      { x: 0.85, y: 0.12, scale: 1.2, speed: 0.00025, opacity: 0.5 },
    ];
  }

  private initParticles(): void {
    this.particles = [];
    for (let i = 0; i < 30; i++) {
      this.particles.push({
        x: Math.random(),
        y: Math.random(),
        vx: (Math.random() - 0.5) * 0.0003,
        vy: (Math.random() - 0.5) * 0.0002 - 0.0001,
        size: 1 + Math.random() * 2,
        phase: Math.random() * Math.PI * 2
      });
    }
  }

  setSize(width: number, height: number): void {
    if (this.width === width && this.height === height) return;
    this.width = width;
    this.height = height;

    if (this.soilCanvas) {
      this.soilCanvas.width = width;
      this.soilCanvas.height = height;
    }
    if (this.hillsCanvas) {
      this.hillsCanvas.width = width;
      this.hillsCanvas.height = height;
    }

    this.soilDirty = true;
    this.hillsDirty = true;
    this.initDecorations();
  }

  private initDecorations(): void {
    if (this.width === 0 || this.height === 0) return;
    const soilTop = this.height * 0.75;
    let seed = this.decorSeed;
    const rand = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };

    const plantAvoidZones = this.gardenPlants.map(p => ({
      left: p.x * this.width - 50,
      right: p.x * this.width + 50,
    }));

    const randomX = (): number => {
      let x = rand() * this.width;
      let attempts = 0;
      while (attempts < 50 && plantAvoidZones.some(z => x > z.left && x < z.right)) {
        x = rand() * this.width;
        attempts++;
      }
      return x;
    };

    const stoneColors = [
      { color: '#8a8a82', dark: '#6a6a62' },
      { color: '#9a9590', dark: '#7a7570' },
      { color: '#7a7a72', dark: '#5a5a52' },
      { color: '#8a8580', dark: '#6a6560' },
      { color: '#9a9a92', dark: '#7a7a72' },
      { color: '#7a8a82', dark: '#5a6a62' },
      { color: '#8a7a6a', dark: '#6a5a4a' },
      { color: '#9a8a7a', dark: '#7a6a5a' },
    ];

    this.stones = [];
    for (let i = 0; i < 18; i++) {
      const c = stoneColors[Math.floor(rand() * stoneColors.length)];
      this.stones.push({
        x: randomX(),
        y: soilTop + 2 + rand() * 6,
        rx: 4 + rand() * 8,
        ry: 2 + rand() * 4,
        color: c.color,
        dark: c.dark,
      });
    }

    const flowerColors = ['#e8d8c0', '#f0e0a8', '#d8e8d0', '#f0d8c8', '#e0e8f0', '#f8e8b8', '#f0c8c0', '#e0f0d8', '#d8d8e8', '#f0e8d0'];
    this.flowers = [];
    for (let i = 0; i < 12; i++) {
      this.flowers.push({
        x: randomX(),
        y: soilTop - 2 + rand() * 4,
        color: flowerColors[Math.floor(rand() * flowerColors.length)],
        size: 2 + rand() * 2.5,
        phase: rand() * Math.PI * 2,
      });
    }

    this.grassTufts = [];
    for (let i = 0; i < 24; i++) {
      this.grassTufts.push({
        x: randomX(),
        blades: 2 + Math.floor(rand() * 4),
        h: 5 + rand() * 12,
        phase: rand() * Math.PI * 2,
      });
    }
  }

  private gardenPlants: Array<{ x: number; y: number }> = [];
  private growLightPlants: Array<{ x: number; y: number; intensity: number }> = [];
  private fireflies: Firefly[] = [];
  private petals: Petal[] = [];
  private wetSpots: WetSpot[] = [];
  private petalSpawnTimer: number = 0;
  private fireflySeed: number = 789;
  private mouseX: number = -1;
  private mouseY: number = -1;

  setMousePos(x: number, y: number): void {
    this.mouseX = x;
    this.mouseY = y;
  }

  setPlants(plants: Array<{ x: number; y: number }>): void {
    if (this.gardenPlants.length === plants.length &&
        this.gardenPlants.every((p, i) => p.x === plants[i].x && p.y === plants[i].y)) return;
    this.gardenPlants = plants;
    this.initDecorations();
  }

  setGrowLightPlants(plants: Array<{ x: number; y: number; intensity: number }>): void {
    this.growLightPlants = plants;
  }

  render(env: Environment, time: number, deltaTime: number = 0): void {
    const soilTop = this.height * 0.75;

    // Layer 0: 天空
    this.renderSky(env);

    // Layer 1: 太阳/月亮
    this.renderSunMoon(env, time);

    // Layer 2: 远景山脉
    this.renderDistantMountains(env);

    // Layer 3: 晨雾
    this.renderMist(env);

    // Layer 4: 体积光（仅在太阳附近）
    this.renderGodRays(env, time);

    // Layer 5: 星星/云朵
    this.renderSkyObjects(env, time);

    // Layer 6: 中景山丘
    this.renderHills();

    // Layer 6.5: 背景灌木丛
    this.renderBackgroundBushes(soilTop);

    // Layer 7: 地面草坡
    this.renderGrassSlope(soilTop, time);

    // Layer 8: 泥土
    this.renderSoil();

    // Layer 9: 湿斑
    this.renderWetSpots(time);

    // Layer 10: 地面装饰
    this.renderGardenDecorations(env, time);

    // Layer 11: 植物投影
    this.renderPlantShadows(soilTop, time);

    // Layer 12: 粒子/花瓣
    this.renderParticles(env, time);
    this.updateAndRenderPetals(time, deltaTime);

    // Layer 13: 萤火虫
    this.renderFireflies(env, time, deltaTime);

    // Layer 14: 前景草丛
    this.renderForegroundGrass(soilTop, time);

    // Layer 15: 暗角
    this.renderVignette();
  }

  private renderSky(env: Environment): void {
    const skyColor = env.getDisplaySkyColor();
    const soilTop = this.height * 0.75;
    const hour = env.timeOfDay;

    // 基础天空渐变
    this.ctx.fillStyle = skyColor;
    this.ctx.fillRect(0, 0, this.width, soilTop);

    // 顶部到地平线的柔和过渡
    const gradient = this.ctx.createLinearGradient(0, 0, 0, soilTop);
    if (hour >= 6 && hour <= 18) {
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.2)');
      gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.08)');
      gradient.addColorStop(0.8, 'rgba(255, 248, 230, 0.03)');
      gradient.addColorStop(1, 'rgba(255, 248, 230, 0)');
    } else {
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.05)');
      gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.02)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    }
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, this.width, soilTop);

    // 地平线暖色带
    const horizonGradient = this.ctx.createLinearGradient(0, soilTop - 80, 0, soilTop);
    const [hr, hg, hb] = env.getDisplayHorizonRGB();
    horizonGradient.addColorStop(0, `rgba(${hr}, ${hg}, ${hb}, 0)`);
    horizonGradient.addColorStop(0.6, `rgba(${hr}, ${hg}, ${hb}, 0.2)`);
    horizonGradient.addColorStop(1, `rgba(${hr}, ${hg}, ${hb}, 0.45)`);
    this.ctx.fillStyle = horizonGradient;
    this.ctx.fillRect(0, soilTop - 80, this.width, 80);

    // 多云天气：天空加一层淡灰
    if (env.weather === 'cloudy') {
      const cloudGrad = this.ctx.createLinearGradient(0, 0, 0, soilTop);
      cloudGrad.addColorStop(0, 'rgba(200, 210, 220, 0.15)');
      cloudGrad.addColorStop(0.5, 'rgba(210, 215, 225, 0.1)');
      cloudGrad.addColorStop(1, 'rgba(220, 225, 235, 0)');
      this.ctx.fillStyle = cloudGrad;
      this.ctx.fillRect(0, 0, this.width, soilTop);
    }
  }

  private renderSkyObjects(env: Environment, time: number): void {
    const nightAlpha = env.displayLightLevel < 10 ? 1 - env.displayLightLevel / 10 : 0;
    const dayAlpha = env.displayLightLevel > 0 ? Math.min(1, env.displayLightLevel / 30) : 0;

    if (nightAlpha > 0.01) {
      this.renderStars(time, nightAlpha);
    }
    if (dayAlpha > 0.01) {
      this.renderClouds(time, dayAlpha);
    }
  }

  private renderStars(time: number, masterAlpha: number): void {
    this.ctx.fillStyle = '#ffffff';
    this.stars.forEach(star => {
      const twinkle = 0.5 + 0.5 * Math.sin(time * 0.002 + star.phase);
      this.ctx.globalAlpha = twinkle * 0.8 * masterAlpha;
      this.ctx.beginPath();
      this.ctx.arc(star.x * this.width, star.y * this.height, star.size, 0, Math.PI * 2);
      this.ctx.fill();
    });
    this.ctx.globalAlpha = 1.0;
  }

  private renderClouds(time: number, masterAlpha: number): void {
    this.clouds.forEach(cloud => {
      const x = ((cloud.x + time * cloud.speed) % 1.2 - 0.1) * this.width;
      const y = cloud.y * this.height;
      const s = cloud.scale;

      this.ctx.globalAlpha = cloud.opacity * masterAlpha;
      this.ctx.fillStyle = '#ffffff';

      this.ctx.beginPath();
      this.ctx.arc(x, y, 25 * s, 0, Math.PI * 2);
      this.ctx.arc(x + 20 * s, y - 8 * s, 20 * s, 0, Math.PI * 2);
      this.ctx.arc(x + 35 * s, y, 22 * s, 0, Math.PI * 2);
      this.ctx.arc(x + 15 * s, y + 5 * s, 18 * s, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.globalAlpha = cloud.opacity * 0.3 * masterAlpha;
      this.ctx.fillStyle = '#e0e8f0';
      this.ctx.beginPath();
      this.ctx.arc(x + 5 * s, y + 8 * s, 22 * s, 0, Math.PI * 2);
      this.ctx.arc(x + 25 * s, y, 18 * s, 0, Math.PI * 2);
      this.ctx.fill();
    });
    this.ctx.globalAlpha = 1.0;
  }

  private renderHills(): void {
    if (this.hillsDirty && this.hillsCanvas && this.hillsCtx) {
      this.prerenderHills();
      this.hillsDirty = false;
    }
    if (this.hillsCanvas) {
      this.ctx.drawImage(this.hillsCanvas, 0, 0);
    }
  }

  private prerenderHills(): void {
    if (!this.hillsCtx || !this.hillsCanvas) return;
    const ctx = this.hillsCtx;
    const width = this.hillsCanvas.width;
    const soilTop = this.height * 0.75;

    ctx.clearRect(0, 0, width, this.hillsCanvas.height);

    ctx.fillStyle = 'rgba(100, 110, 130, 0.25)';
    ctx.beginPath();
    ctx.moveTo(0, soilTop);
    for (let x = 0; x <= width; x += 10) {
      const y = soilTop - 30 - Math.sin(x * 0.008) * 18 - Math.sin(x * 0.02 + 1) * 10;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(width, soilTop);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = 'rgba(90, 85, 75, 0.35)';
    ctx.beginPath();
    ctx.moveTo(0, soilTop);
    for (let x = 0; x <= width; x += 10) {
      const y = soilTop - 15 - Math.sin(x * 0.012 + 2) * 12 - Math.sin(x * 0.03) * 6;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(width, soilTop);
    ctx.closePath();
    ctx.fill();
  }

  private renderSoil(): void {
    if (this.soilDirty && this.soilCanvas && this.soilCtx) {
      this.prerenderSoil();
      this.soilDirty = false;
    }
    if (this.soilCanvas) {
      this.ctx.drawImage(this.soilCanvas, 0, 0);
    }
  }

  private prerenderSoil(): void {
    if (!this.soilCtx || !this.soilCanvas) return;
    const soilTop = this.height * 0.75;
    const ctx = this.soilCtx;
    const width = this.soilCanvas.width;
    const height = this.soilCanvas.height;

    ctx.clearRect(0, 0, width, height);

    const soilGradient = ctx.createLinearGradient(0, soilTop, 0, height);
    soilGradient.addColorStop(0, '#4a3a2a');
    soilGradient.addColorStop(0.3, '#3d2e1f');
    soilGradient.addColorStop(1, '#2a1e12');
    ctx.fillStyle = soilGradient;
    ctx.fillRect(0, soilTop, width, height - soilTop);

    const particles = this.generateSoilParticles();
    particles.forEach(particle => {
      const x = particle.x * width;
      const y = soilTop + particle.y * (height - soilTop);
      ctx.fillStyle = particle.color;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.arc(x, y, particle.size, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalAlpha = 1.0;

    ctx.strokeStyle = '#3a2a1a';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, soilTop);
    for (let x = 0; x < width; x += 5) {
      const jitter = Math.sin(x * 0.05) * 2;
      ctx.lineTo(x, soilTop + jitter);
    }
    ctx.stroke();

    ctx.strokeStyle = '#5a4a3a';
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.35;
    for (let i = 0; i < 40; i++) {
      const rx = (i / 40) * width + Math.sin(i * 3) * 20;
      ctx.beginPath();
      ctx.moveTo(rx, soilTop + 3);
      ctx.quadraticCurveTo(rx + Math.sin(i) * 10, soilTop + 15, rx + Math.cos(i) * 5, soilTop + 25);
      ctx.stroke();
    }
    ctx.globalAlpha = 1.0;

    // 草根纹理（土壤最上层）
    ctx.strokeStyle = '#5a6a4a';
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.25;
    for (let i = 0; i < 80; i++) {
      const rx = (i / 80) * width + Math.sin(i * 4.7) * 25;
      const rootLen = 6 + Math.sin(i * 2.3) * 8;
      const rootCurve = Math.sin(i * 3.1) * 4;
      ctx.beginPath();
      ctx.moveTo(rx, soilTop + 2);
      ctx.quadraticCurveTo(rx + rootCurve, soilTop + rootLen * 0.5, rx + rootCurve * 0.5, soilTop + rootLen);
      ctx.stroke();
    }
    ctx.globalAlpha = 1.0;
  }

  private renderGardenDecorations(_env: Environment, time: number): void {
    this.renderStones();
    this.renderSmallFlowers(time);
    this.renderGrassTufts(time);
  }

  private renderStones(): void {
    for (const s of this.stones) {
      this.ctx.fillStyle = s.dark;
      this.ctx.beginPath();
      this.ctx.ellipse(s.x + 1, s.y + 1, s.rx, s.ry, 0, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.fillStyle = s.color;
      this.ctx.beginPath();
      this.ctx.ellipse(s.x, s.y, s.rx, s.ry, 0, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      this.ctx.ellipse(s.x - s.rx * 0.2, s.y - s.ry * 0.2, s.rx * 0.5, s.ry * 0.4, -0.3, 0, Math.PI);
      this.ctx.stroke();
    }
  }

  private renderSmallFlowers(time: number): void {
    for (const f of this.flowers) {
      const sway = Math.sin(time * 0.001 + f.phase) * 1.5;

      this.ctx.strokeStyle = '#5a6a4a';
      this.ctx.lineWidth = 1.5;
      this.ctx.beginPath();
      this.ctx.moveTo(f.x, f.y + 4);
      this.ctx.quadraticCurveTo(f.x + sway * 0.5, f.y + 10, f.x + sway, f.y + 14);
      this.ctx.stroke();

      this.ctx.fillStyle = '#4a6a3a';
      this.ctx.beginPath();
      this.ctx.ellipse(f.x - 2, f.y + 8, 2, 1, 0.3, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.beginPath();
      this.ctx.ellipse(f.x + 2, f.y + 7, 2, 1, -0.3, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.fillStyle = f.color;
      this.ctx.beginPath();
      this.ctx.arc(f.x + sway, f.y, f.size, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.fillStyle = '#ffffff';
      this.ctx.globalAlpha = 0.4;
      this.ctx.beginPath();
      this.ctx.arc(f.x + sway - f.size * 0.3, f.y - f.size * 0.3, f.size * 0.4, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.globalAlpha = 1.0;
    }
  }

  private renderGrassTufts(time: number): void {
    for (const tuft of this.grassTufts) {
      for (let i = 0; i < tuft.blades; i++) {
        const offset = (i - tuft.blades / 2) * 3;
        const sway = Math.sin(time * 0.0015 + tuft.phase + i) * 2;
        const h = tuft.h + Math.sin(i * 2) * 3;
        const soilTop = this.height * 0.75;

        this.ctx.strokeStyle = '#6a8a5a';
        this.ctx.lineWidth = 1.2;
        this.ctx.beginPath();
        this.ctx.moveTo(tuft.x + offset, soilTop + 2);
        this.ctx.quadraticCurveTo(
          tuft.x + offset + sway * 0.5,
          soilTop + 2 - h * 0.5,
          tuft.x + offset + sway,
          soilTop + 2 - h
        );
        this.ctx.stroke();
      }
    }
  }

  private renderParticles(env: Environment, time: number): void {
    const isNight = env.timeOfDay < 5 || env.timeOfDay > 20;
    const soilTop = this.height * 0.75;

    this.particles.forEach(p => {
      let x = (p.x + time * p.vx) % 1.0;
      if (x < 0) x += 1.0;
      let y = (p.y + time * p.vy) % 1.0;
      if (y < 0) y += 1.0;

      const px = x * this.width;
      const py = y * (this.height * 0.8) + this.height * 0.1;

      if (py > soilTop + 10) return;

      const pulse = 0.3 + 0.3 * Math.sin(time * 0.001 + p.phase);

      if (isNight) {
        this.ctx.fillStyle = `rgba(200, 220, 255, ${pulse})`;
      } else {
        this.ctx.fillStyle = `rgba(255, 240, 200, ${pulse * 0.6})`;
      }

      this.ctx.beginPath();
      this.ctx.arc(px, py, p.size, 0, Math.PI * 2);
      this.ctx.fill();
    });
  }

  private generateSoilParticles(): Array<{ x: number; y: number; size: number; color: string }> {
    const particles: Array<{ x: number; y: number; size: number; color: string }> = [];
    const count = 200;
    let seed = 42;
    const random = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };

    for (let i = 0; i < count; i++) {
      particles.push({
        x: random(),
        y: random(),
        size: 1 + random() * 3,
        color: this.getRandomSoilColor(random)
      });
    }
    return particles;
  }

  private getRandomSoilColor(random: () => number): string {
    const colors = ['#5a4a3a', '#6a5a4a', '#7a6a5a', '#4a3a2a', '#8a7a6a'];
    return colors[Math.floor(random() * colors.length)];
  }

  renderGrowLights(time: number): void {
    if (this.growLightPlants.length === 0) return;

    const soilTop = this.height * 0.75;
    const maxIntensity = Math.min(1, this.growLightPlants.reduce((max, p) => Math.max(max, p.intensity), 0));

    // 全局顶部补光氛围层——柔和地覆盖整个天空区域
    const globalGrad = this.ctx.createLinearGradient(0, 0, 0, soilTop);
    globalGrad.addColorStop(0, `rgba(255, 240, 200, ${0.05 * maxIntensity})`);
    globalGrad.addColorStop(0.4, `rgba(255, 240, 200, ${0.025 * maxIntensity})`);
    globalGrad.addColorStop(1, 'rgba(255, 240, 200, 0)');
    this.ctx.fillStyle = globalGrad;
    this.ctx.fillRect(0, 0, this.width, soilTop);

    // 每株植物上方的局部聚光光晕——径向渐变，小而柔和
    for (const plant of this.growLightPlants) {
      const px = plant.x * this.width;
      const py = plant.y * this.height;
      const intensity = plant.intensity;
      if (intensity <= 0) continue;

      const flicker = 0.02 * Math.sin(time * 0.003 + px);
      const glowRadius = 22 + intensity * 12;

      const glow = this.ctx.createRadialGradient(
        px, py - 8, 0,
        px, py - 8, glowRadius
      );
      glow.addColorStop(0, `rgba(255, 242, 210, ${0.22 * intensity + flicker})`);
      glow.addColorStop(0.5, `rgba(255, 235, 195, ${0.1 * intensity})`);
      glow.addColorStop(1, 'rgba(255, 230, 180, 0)');

      this.ctx.fillStyle = glow;
      this.ctx.beginPath();
      this.ctx.arc(px, py - 8, glowRadius, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }

  private renderVignette(): void {
    const gradient = this.ctx.createRadialGradient(
      this.width * 0.5, this.height * 0.4, this.height * 0.35,
      this.width * 0.5, this.height * 0.4, this.height * 0.8
    );
    gradient.addColorStop(0, 'rgba(20, 15, 10, 0)');
    gradient.addColorStop(0.7, 'rgba(20, 15, 10, 0.02)');
    gradient.addColorStop(1, 'rgba(20, 15, 10, 0.08)');
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, this.width, this.height);
  }

  private renderMist(env: Environment): void {
    const hour = env.timeOfDay;
    if (hour < 4 || hour > 7) return;

    const mistAlpha = hour < 5.5
      ? (hour - 4) / 1.5 * 0.12
      : (7 - hour) / 1.5 * 0.12;
    if (mistAlpha <= 0.005) return;

    const soilTop = this.height * 0.75;
    const mistGradient = this.ctx.createLinearGradient(0, soilTop - 60, 0, soilTop + 30);
    mistGradient.addColorStop(0, `rgba(240, 235, 225, 0)`);
    mistGradient.addColorStop(0.4, `rgba(240, 235, 225, ${mistAlpha})`);
    mistGradient.addColorStop(0.8, `rgba(235, 230, 220, ${mistAlpha * 0.6})`);
    mistGradient.addColorStop(1, `rgba(240, 235, 225, 0)`);
    this.ctx.fillStyle = mistGradient;
    this.ctx.fillRect(0, soilTop - 60, this.width, 90);

    for (let i = 0; i < 5; i++) {
      const mx = ((i / 5) + Math.sin(hour * 3 + i) * 0.05) * this.width;
      const my = soilTop - 20 + Math.sin(i * 2.3) * 10;
      const mw = 120 + Math.sin(i * 1.7) * 40;
      const mh = 30 + Math.cos(i * 2.1) * 15;
      const blobGrad = this.ctx.createRadialGradient(mx, my, 0, mx, my, mw);
      blobGrad.addColorStop(0, `rgba(245, 240, 230, ${mistAlpha * 0.5})`);
      blobGrad.addColorStop(1, `rgba(245, 240, 230, 0)`);
      this.ctx.fillStyle = blobGrad;
      this.ctx.beginPath();
      this.ctx.ellipse(mx, my, mw, mh, 0, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }

  private renderGodRays(env: Environment, time: number): void {
    const sun = env.getSunPosition();
    const sunOp = env.getSunOpacity();
    if (!sun.visible || sunOp <= 0.1) return;

    const soilTop = this.height * 0.75;
    const intensity = Math.min(1, env.displayLightLevel / 80) * 0.025 * sunOp;
    if (intensity <= 0.002) return;

    const sunX = sun.x * this.width;
    const sunY = sun.y * soilTop;
    const rayCount = 3;

    for (let i = 0; i < rayCount; i++) {
      const angleBase = -0.35 + i * 0.35;
      const angle = angleBase + Math.sin(time * 0.00015 + i * 2) * 0.04;
      const length = soilTop + 30;
      const widthTop = 12 + i * 6;
      const widthBottom = 50 + i * 25;

      const dx = Math.sin(angle);
      const dy = Math.cos(angle);

      const rayGrad = this.ctx.createLinearGradient(
        sunX, sunY,
        sunX + dx * length, sunY + dy * length
      );
      rayGrad.addColorStop(0, `rgba(255, 248, 230, ${intensity})`);
      rayGrad.addColorStop(0.4, `rgba(255, 245, 220, ${intensity * 0.5})`);
      rayGrad.addColorStop(1, `rgba(255, 240, 210, 0)`);

      this.ctx.fillStyle = rayGrad;
      this.ctx.beginPath();
      this.ctx.moveTo(sunX - widthTop * 0.5, sunY);
      this.ctx.lineTo(sunX + widthTop * 0.5, sunY);
      this.ctx.lineTo(sunX + dx * length + widthBottom * 0.5, sunY + dy * length);
      this.ctx.lineTo(sunX + dx * length - widthBottom * 0.5, sunY + dy * length);
      this.ctx.closePath();
      this.ctx.fill();
    }
  }

  private renderFireflies(env: Environment, time: number, deltaTime: number = 0): void {
    const nightAlpha = env.displayLightLevel < 15 ? 1 - env.displayLightLevel / 15 : 0;
    if (nightAlpha <= 0.01) return;

    const dt = Math.min(deltaTime || 0, 1);
    const minDim = Math.min(this.width, this.height);
    const repelRadius = minDim > 0 ? 120 / minDim : 0.15;

    this.fireflies.forEach(ff => {
      if (dt > 0) {
        // 鼠标排斥
        if (this.mouseX >= 0 && this.mouseY >= 0 && repelRadius > 0) {
          const mx = this.mouseX / this.width;
          const my = this.mouseY / this.height;
          const dx = ff.x - mx;
          const dy = ff.y - my;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < repelRadius && dist > 0.001) {
            const force = (1 - dist / repelRadius) * 0.0003;
            ff.vx += (dx / dist) * force;
            ff.vy += (dy / dist) * force;
          }
        }

        // 恢复原始漂移并施加阻尼
        ff.vx += (ff.baseVx - ff.vx) * 0.03;
        ff.vy += (ff.baseVy - ff.vy) * 0.03;

        ff.x = ((ff.x + ff.vx * dt) % 1.0 + 1.0) % 1.0;
        ff.y = ((ff.y + ff.vy * dt) % 1.0 + 1.0) % 1.0;

        // 保持在天空区域
        if (ff.y > 0.7) { ff.y = 0.7; ff.vy *= -0.5; }
        if (ff.y < 0.15) { ff.y = 0.15; ff.vy *= -0.5; }
      }

      const px = ff.x * this.width;
      const py = ff.y * this.height;

      const blink = 0.3 + 0.7 * Math.sin(time * 0.0015 * ff.speed + ff.phase);
      const alpha = blink * nightAlpha * 0.45;
      const glowSize = 6 + 3 * blink;

      const glow = this.ctx.createRadialGradient(px, py, 0, px, py, glowSize);
      glow.addColorStop(0, `rgba(255, 238, 180, ${alpha})`);
      glow.addColorStop(0.4, `rgba(255, 232, 160, ${alpha * 0.4})`);
      glow.addColorStop(1, `rgba(255, 224, 140, 0)`);
      this.ctx.fillStyle = glow;
      this.ctx.beginPath();
      this.ctx.arc(px, py, glowSize, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.fillStyle = `rgba(255, 248, 220, ${alpha * 0.6 + 0.05})`;
      this.ctx.beginPath();
      this.ctx.arc(px, py, 1, 0, Math.PI * 2);
      this.ctx.fill();
    });
  }

  private updateAndRenderPetals(time: number, deltaTime: number): void {
    if (deltaTime > 0) {
      this.petalSpawnTimer += deltaTime;
    }

    this.petals = this.petals.filter(p => {
      p.y += p.vy;
      p.x += p.vx + Math.sin(time * 0.001 + p.rot) * 0.3;
      p.rot += p.rotSpeed;
      p.alpha -= 0.0015;
      return p.alpha > 0 && p.y < this.height + 20;
    });

    this.petals.forEach(p => {
      this.ctx.save();
      this.ctx.globalAlpha = p.alpha;
      this.ctx.translate(p.x, p.y);
      this.ctx.rotate(p.rot);
      this.ctx.fillStyle = p.color;
      this.ctx.beginPath();
      this.ctx.ellipse(0, 0, p.size * 0.6, p.size, 0, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.restore();
    });
  }

  private renderWetSpots(time: number): void {
    this.wetSpots = this.wetSpots.filter(s => {
      s.alpha -= 0.0007;
      return s.alpha > 0.005;
    });

    this.wetSpots.forEach(s => {
      const wetGrad = this.ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.radius);
      wetGrad.addColorStop(0, `rgba(60, 50, 40, ${s.alpha * 0.5})`);
      wetGrad.addColorStop(0.6, `rgba(55, 45, 35, ${s.alpha * 0.3})`);
      wetGrad.addColorStop(1, `rgba(50, 40, 30, 0)`);
      this.ctx.fillStyle = wetGrad;
      this.ctx.beginPath();
      this.ctx.ellipse(s.x, s.y, s.radius, s.radius * 0.5, 0, 0, Math.PI * 2);
      this.ctx.fill();

      s.droplets.forEach(d => {
        const dx = s.x + d.x;
        const dy = s.y + d.y;
        const shimmer = 0.3 + 0.3 * Math.sin(time * 0.003 + d.phase);
        this.ctx.fillStyle = `rgba(255, 255, 255, ${s.alpha * shimmer * 0.6})`;
        this.ctx.beginPath();
        this.ctx.ellipse(dx, dy, d.size, d.size * 0.7, 0, 0, Math.PI * 2);
        this.ctx.fill();
      });
    });
  }

  private renderSunMoon(env: Environment, _time: number): void {
    const soilTop = this.height * 0.75;

    // 太阳
    const sun = env.getSunPosition();
    const sunOp = env.getSunOpacity();
    if (sun.visible && sunOp > 0.01) {
      const sx = sun.x * this.width;
      const sy = sun.y * soilTop;
      const sr = 28;

      const glow = this.ctx.createRadialGradient(sx, sy, sr * 0.5, sx, sy, sr * 4);
      glow.addColorStop(0, `rgba(255, 248, 220, ${0.25 * sunOp})`);
      glow.addColorStop(0.3, `rgba(255, 240, 200, ${0.12 * sunOp})`);
      glow.addColorStop(1, `rgba(255, 230, 180, 0)`);
      this.ctx.fillStyle = glow;
      this.ctx.beginPath();
      this.ctx.arc(sx, sy, sr * 4, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.globalAlpha = sunOp;
      this.ctx.fillStyle = '#fff8e0';
      this.ctx.beginPath();
      this.ctx.arc(sx, sy, sr, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.fillStyle = `rgba(255, 240, 180, ${0.4 * sunOp})`;
      this.ctx.beginPath();
      this.ctx.arc(sx - 4, sy - 3, sr * 0.85, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.globalAlpha = 1.0;
    }

    // 月亮
    const moon = env.getMoonPosition();
    const moonOp = env.getMoonOpacity();
    if (moon.visible && moonOp > 0.01) {
      const mx = moon.x * this.width;
      const my = moon.y * soilTop;
      const mr = 22;

      const glow = this.ctx.createRadialGradient(mx, my, mr, mx, my, mr * 3);
      glow.addColorStop(0, `rgba(220, 220, 240, ${0.15 * moonOp})`);
      glow.addColorStop(1, `rgba(220, 220, 240, 0)`);
      this.ctx.fillStyle = glow;
      this.ctx.beginPath();
      this.ctx.arc(mx, my, mr * 3, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.globalAlpha = moonOp;
      this.ctx.fillStyle = '#e8e8f0';
      this.ctx.beginPath();
      this.ctx.arc(mx, my, mr, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.fillStyle = `rgba(40, 45, 60, ${0.25 * moonOp})`;
      this.ctx.beginPath();
      this.ctx.arc(mx + 6, my - 3, mr * 0.9, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.globalAlpha = 1.0;
    }
  }

  private renderDistantMountains(env: Environment): void {
    const soilTop = this.height * 0.75;
    const baseAlpha = env.displayLightLevel < 15 ? 0.08 : 0.12;

    this.ctx.fillStyle = `rgba(140, 150, 170, ${baseAlpha})`;
    this.ctx.beginPath();
    this.ctx.moveTo(0, soilTop);
    for (let x = 0; x <= this.width; x += 8) {
      const y = soilTop - 50 - Math.sin(x * 0.004) * 25 - Math.sin(x * 0.012 + 2) * 15;
      this.ctx.lineTo(x, y);
    }
    this.ctx.lineTo(this.width, soilTop);
    this.ctx.closePath();
    this.ctx.fill();

    this.ctx.fillStyle = `rgba(120, 130, 150, ${baseAlpha * 0.7})`;
    this.ctx.beginPath();
    this.ctx.moveTo(0, soilTop);
    for (let x = 0; x <= this.width; x += 8) {
      const y = soilTop - 35 - Math.sin(x * 0.006 + 1) * 18 - Math.sin(x * 0.018) * 10;
      this.ctx.lineTo(x, y);
    }
    this.ctx.lineTo(this.width, soilTop);
    this.ctx.closePath();
    this.ctx.fill();
  }

  private renderGrassSlope(soilTop: number, time: number): void {
    const slopeHeight = 18;
    const grassColors = ['#7a9a6a', '#6a8a5a', '#8aaa7a', '#5a7a4a'];

    // 基础草坡
    this.ctx.fillStyle = '#6a8a5a';
    this.ctx.beginPath();
    this.ctx.moveTo(0, soilTop);
    for (let x = 0; x <= this.width; x += 5) {
      const y = soilTop - slopeHeight - Math.sin(x * 0.008 + 0.5) * 6 - Math.sin(x * 0.02) * 3;
      this.ctx.lineTo(x, y);
    }
    this.ctx.lineTo(this.width, soilTop);
    this.ctx.closePath();
    this.ctx.fill();

    // 草坡纹理
    for (let i = 0; i < 60; i++) {
      const gx = (i / 60) * this.width + Math.sin(i * 2.7) * 30;
      const baseY = soilTop - slopeHeight - Math.sin(gx * 0.008 + 0.5) * 6;
      const sway = Math.sin(time * 0.001 + i) * 1.5;
      const h = 4 + Math.sin(i * 3.1) * 5;
      this.ctx.strokeStyle = grassColors[i % 4];
      this.ctx.lineWidth = 1.2;
      this.ctx.globalAlpha = 0.5;
      this.ctx.beginPath();
      this.ctx.moveTo(gx, baseY + 3);
      this.ctx.quadraticCurveTo(gx + sway, baseY - h * 0.5, gx + sway * 1.5, baseY - h);
      this.ctx.stroke();
    }
    this.ctx.globalAlpha = 1.0;
  }

  private renderPlantShadows(soilTop: number, _time: number): void {
    for (const plant of this.gardenPlants) {
      const px = plant.x * this.width;
      const py = plant.y * this.height;
      if (py < soilTop - 10) {
        const shadowY = soilTop + 2;
        const dist = shadowY - py;
        const sizeX = 18 + dist * 0.15;
        const sizeY = 5 + dist * 0.05;
        const alpha = 0.12 - dist * 0.0003;
        if (alpha > 0) {
          this.ctx.fillStyle = `rgba(30, 25, 20, ${alpha})`;
          this.ctx.beginPath();
          this.ctx.ellipse(px + 3, shadowY, sizeX, sizeY, 0, 0, Math.PI * 2);
          this.ctx.fill();
        }
      }
    }
  }

  private renderForegroundGrass(_soilTop: number, time: number): void {
    const fgY = this.height - 8;

    // 深色剪影草丛
    for (let i = 0; i < 40; i++) {
      const gx = (i / 40) * this.width + Math.sin(i * 4.3) * 15;
      const sway = Math.sin(time * 0.0008 + i * 0.5) * 2;
      const h = 12 + Math.sin(i * 2.7) * 10;
      const w = 2 + Math.sin(i * 3.1) * 1.5;

      this.ctx.fillStyle = `rgba(25, 35, 20, ${0.5 + Math.sin(i) * 0.2})`;
      this.ctx.beginPath();
      this.ctx.moveTo(gx - w, fgY);
      this.ctx.quadraticCurveTo(gx + sway * 0.5, fgY - h * 0.5, gx + sway, fgY - h);
      this.ctx.quadraticCurveTo(gx + sway * 0.3, fgY - h * 0.4, gx + w, fgY);
      this.ctx.closePath();
      this.ctx.fill();
    }

    // 底部模糊带
    const blurGrad = this.ctx.createLinearGradient(0, fgY - 5, 0, this.height);
    blurGrad.addColorStop(0, 'rgba(25, 35, 20, 0)');
    blurGrad.addColorStop(0.5, 'rgba(25, 35, 20, 0.15)');
    blurGrad.addColorStop(1, 'rgba(20, 30, 15, 0.35)');
    this.ctx.fillStyle = blurGrad;
    this.ctx.fillRect(0, fgY - 5, this.width, this.height - fgY + 5);
  }

  private renderBackgroundBushes(soilTop: number): void {
    // 远山针叶林剪影——贴合远山轮廓，不悬浮
    const trees = [
      { x: 0.03, topOff: -75, w: 16, h: 60 },
      { x: 0.06, topOff: -62, w: 22, h: 72 },
      { x: 0.10, topOff: -80, w: 14, h: 55 },
      { x: 0.14, topOff: -68, w: 18, h: 65 },
      { x: 0.90, topOff: -70, w: 20, h: 68 },
      { x: 0.94, topOff: -58, w: 16, h: 52 },
      { x: 0.97, topOff: -78, w: 24, h: 75 },
      { x: 0.22, topOff: -65, w: 12, h: 48 },
      { x: 0.78, topOff: -72, w: 15, h: 58 },
    ];

    this.ctx.fillStyle = '#252e22';

    for (const t of trees) {
      const bx = t.x * this.width;
      const topY = soilTop + t.topOff;
      const hw = t.w / 2;
      const h = t.h;

      // 主干层
      this.ctx.globalAlpha = 0.22;
      this.ctx.beginPath();
      this.ctx.moveTo(bx - hw, topY + h);
      this.ctx.quadraticCurveTo(bx - hw * 0.3, topY + h * 0.6, bx, topY);
      this.ctx.quadraticCurveTo(bx + hw * 0.3, topY + h * 0.6, bx + hw, topY + h);
      this.ctx.closePath();
      this.ctx.fill();

      // 树冠层（更窄更尖）
      this.ctx.globalAlpha = 0.14;
      const cw = hw * 0.65;
      this.ctx.beginPath();
      this.ctx.moveTo(bx - cw, topY + h * 0.45);
      this.ctx.quadraticCurveTo(bx - cw * 0.3, topY + h * 0.2, bx, topY - h * 0.1);
      this.ctx.quadraticCurveTo(bx + cw * 0.3, topY + h * 0.2, bx + cw, topY + h * 0.45);
      this.ctx.closePath();
      this.ctx.fill();
    }

    this.ctx.globalAlpha = 1.0;
  }

  getSoilSurfaceY(): number {
    return this.height * 0.75;
  }
}
